import {
  createReference,
  GroupSecurity,
  getReferenceData,
  TaskCode,
} from '@quromedical/fhir-common';
import { first } from '@quromedical/utils';
import { WithGroup } from 'core/auth';
import { Assignment, type AssignmentResult, TaskItem } from 'core/task';
import { Skeleton, SkeletonProvider } from 'design-system';
import { usePatientTasks } from 'hooks/useTasks';
import { useCallback } from 'react';

type TaskColumnFocusType = 'Media' | 'DiagnosticReport';

interface TaskColumnProps {
  patientId: string;
  focusId: string;
  focusType: TaskColumnFocusType;
}

export const TaskColumn: React.FC<TaskColumnProps> = ({
  patientId,
  focusId,
  focusType,
}) => {
  const { create, addNote, isLoading, tasks } = usePatientTasks({
    subject: patientId,
    focus: focusId,
  });

  // We only allow for a resource to have one task at a time
  const task = first(tasks);
  const onChange = useCallback(
    async (result?: AssignmentResult) => {
      if (!result) {
        return;
      }

      const referenceData = getReferenceData({ reference: result.performer });

      if (!referenceData) {
        return;
      }

      await create({
        patientId,
        code: result.code,
        description: result.note,
        performer: createReference(
          referenceData.resourceType,
          referenceData.id,
        ),
        requestedPeriodEnd: result.dueDate
          ? result.dueDate.toString()
          : undefined,
        focus: createReference(focusType, focusId),
      });
    },
    [create, focusId, patientId, focusType],
  );

  const taskCode =
    focusType === 'Media'
      ? TaskCode.MediaFollowUp
      : TaskCode.DiagnosticReportFollowUp;

  return (
    <SkeletonProvider loading={isLoading}>
      <Skeleton>
        {!task ? (
          <WithGroup groups={[GroupSecurity.TaskCreate]}>
            <Assignment
              codes={[taskCode]}
              onChange={onChange}
              patientId={patientId}
            />
          </WithGroup>
        ) : (
          <WithGroup groups={[GroupSecurity.TaskUpdate]}>
            <TaskItem task={task} onNoteAdd={addNote} />
          </WithGroup>
        )}
      </Skeleton>
    </SkeletonProvider>
  );
};
